import type {
  FlipperPart,
  GuideRailPart,
  InsertPart,
  RampPart,
  TableDefinition,
  TablePart,
  Vec2,
  WallPart,
} from './definition';

const WIREFORM_TABLE_IDS = new Set([
  'cosmic-relay-v1',
  'neon-rush-v1',
  'clockwork-district-v1',
  'frontier-run-v1',
  'dragon-keep-v1',
]);

function scalePoint(point: Vec2, sx: number, sy: number): Vec2 {
  return { x: point.x * sx, y: point.y * sy };
}

function scalePart(part: TablePart, sx: number, sy: number): void {
  const radialScale = Math.min(sx, sy);
  const widthScale = sx;
  switch (part.kind) {
    case 'wall':
    case 'guide-rail':
    case 'slingshot':
      part.points = part.points.map((point) => scalePoint(point, sx, sy));
      if ('thickness' in part) part.thickness *= radialScale;
      return;
    case 'post':
    case 'bumper':
    case 'rollover':
    case 'warp':
      part.center = scalePoint(part.center, sx, sy);
      part.radius *= radialScale;
      return;
    case 'insert':
      part.center = scalePoint(part.center, sx, sy);
      part.width *= sx;
      part.height *= sy;
      return;
    case 'target':
      part.center = scalePoint(part.center, sx, sy);
      part.width *= widthScale;
      return;
    case 'kicker':
      part.center = scalePoint(part.center, sx, sy);
      part.ejectVelocity = scalePoint(part.ejectVelocity, sx, sy);
      if (part.radius !== undefined) part.radius *= radialScale;
      return;
    case 'spinner':
      part.pivot = scalePoint(part.pivot, sx, sy);
      part.width *= widthScale;
      return;
    case 'flipper-left':
    case 'flipper-right':
      part.pivot = scalePoint(part.pivot, sx, sy);
      part.length *= widthScale;
      return;
    case 'drain':
      part.center = scalePoint(part.center, sx, sy);
      part.width *= widthScale;
      return;
    case 'ramp':
      part.entry = scalePoint(part.entry, sx, sy);
      part.path = part.path.map((point) => scalePoint(point, sx, sy));
      part.exit = scalePoint(part.exit, sx, sy);
      part.exitVelocity = scalePoint(part.exitVelocity, sx, sy);
      if (part.width !== undefined) part.width *= widthScale;
      return;
    case 'plunger-lane':
      part.bounds = {
        x: part.bounds.x * sx,
        y: part.bounds.y * sy,
        width: part.bounds.width * sx,
        height: part.bounds.height * sy,
      };
      if (part.restPosition) part.restPosition = scalePoint(part.restPosition, sx, sy);
      if (part.divider) {
        part.divider.points = part.divider.points.map((point) => scalePoint(point, sx, sy));
        part.divider.thickness *= radialScale;
      }
      if (part.stop) {
        part.stop.center = scalePoint(part.stop.center, sx, sy);
        part.stop.width *= sx;
        part.stop.thickness *= radialScale;
      }
      if (part.launchPath) part.launchPath = part.launchPath.map((point) => scalePoint(point, sx, sy));
      return;
  }
}

export function resizeTable(table: TableDefinition, width: number, height: number): TableDefinition {
  const sx = width / table.board.width;
  const sy = height / table.board.height;
  table.parts.forEach((part) => scalePart(part, sx, sy));
  table.board.width = width;
  table.board.height = height;
  return table;
}

/**
 * Rebuild the three cabinet boundary walls as one continuous, conventional
 * pinball shell after a table has been resized. The old reference coordinates
 * left several-unit gaps near the lower corners on tall machines; extending
 * the side walls almost to the drain removes those escape/stall pockets while
 * preserving the intentional inlane/outlane and center-drain openings.
 */
export function normalizePlayfieldShell(table: TableDefinition): TableDefinition {
  const width = table.board.width;
  const height = table.board.height;
  const margin = Math.max(1.6, Math.min(width, height) * 0.035);
  const lowerY = height - Math.max(1.2, margin * 0.6);
  const thickness = Math.max(0.9, Math.min(width, height) * 0.019);

  const specs: Array<{ id: string; points: Vec2[] }> = [
    { id: 'wall-left', points: [{ x: margin, y: margin }, { x: margin, y: lowerY }] },
    { id: 'wall-top', points: [{ x: margin, y: margin }, { x: width - margin, y: margin }] },
    { id: 'wall-right', points: [{ x: width - margin, y: margin }, { x: width - margin, y: lowerY }] },
  ];

  for (const spec of specs) {
    const existing = table.parts.find(
      (part): part is WallPart => part.id === spec.id && part.kind === 'wall',
    );
    if (existing) {
      existing.points = spec.points;
      existing.thickness = Math.max(existing.thickness, thickness);
      existing.material = existing.material ?? 'metal';
      continue;
    }
    table.parts.push({
      id: spec.id,
      kind: 'wall',
      scoreValue: 0,
      thickness,
      material: 'metal',
      points: spec.points,
    });
  }

  return table;
}

export function addUpperFlippers(table: TableDefinition): TableDefinition {
  const leftBase = table.parts.find((part): part is FlipperPart => part.kind === 'flipper-left');
  const rightBase = table.parts.find((part): part is FlipperPart => part.kind === 'flipper-right');
  if (!leftBase || !rightBase) return table;

  const upperY = table.board.height * 0.56;
  const left: FlipperPart = {
    ...leftBase,
    id: 'flip-upper-l',
    pivot: { x: table.board.width * 0.31, y: upperY },
    length: Math.max(7.5, table.board.width * 0.13),
    restAngleDeg: 20,
    sweepDeg: 52,
  };
  const right: FlipperPart = {
    ...rightBase,
    id: 'flip-upper-r',
    pivot: { x: table.board.width * 0.69, y: upperY },
    length: Math.max(7.5, table.board.width * 0.13),
    restAngleDeg: 20,
    sweepDeg: 52,
  };
  table.parts.push(left, right);
  return table;
}

export function makeRampReadable(table: TableDefinition): TableDefinition {
  const wireform = WIREFORM_TABLE_IDS.has(table.id);
  for (const part of table.parts) {
    if (part.kind !== 'ramp') continue;
    const x = table.board.width;
    const y = table.board.height;
    part.entry = { x: x * 0.69, y: y * 0.68 };
    part.path = [
      { x: x * 0.69, y: y * 0.68 },
      { x: x * 0.76, y: y * 0.58 },
      { x: x * 0.77, y: y * 0.45 },
      { x: x * 0.73, y: y * 0.33 },
      { x: x * 0.65, y: y * 0.23 },
      { x: x * 0.57, y: y * 0.19 },
    ];
    part.exit = { ...part.path[part.path.length - 1]! };
    part.exitVelocity = { x: -table.board.width * 0.14, y: table.board.height * 0.18 };
    part.style = wireform ? 'wireform' : 'solid';
    part.width = wireform ? Math.max(4.4, x * 0.082) : Math.max(5.2, x * 0.09);
  }
  return table;
}

export function addUpperRamp(table: TableDefinition): TableDefinition {
  const source = table.parts.find((part): part is RampPart => part.kind === 'ramp');
  if (!source) return table;
  const x = table.board.width;
  const y = table.board.height;
  const upper: RampPart = {
    ...source,
    id: 'ramp-upper-loop',
    scoreValue: Math.round(source.scoreValue * 1.4),
    style: 'wireform',
    width: Math.max(4.6, x * 0.075),
    entry: { x: x * 0.29, y: y * 0.53 },
    path: [
      { x: x * 0.29, y: y * 0.53 },
      { x: x * 0.22, y: y * 0.44 },
      { x: x * 0.22, y: y * 0.32 },
      { x: x * 0.3, y: y * 0.22 },
      { x: x * 0.43, y: y * 0.17 },
      { x: x * 0.52, y: y * 0.2 },
    ],
    exit: { x: x * 0.52, y: y * 0.2 },
    exitVelocity: { x: x * 0.12, y: y * 0.16 },
  };
  table.parts.push(upper);
  return table;
}

/**
 * Turns the extra-long Haunted Keep cabinet into two distinct playable stages
 * rather than a uniformly stretched reference layout. Mechanisms are grouped
 * into an upper battle zone and a lower recovery/shot zone, while both ramp
 * mouths stay clear of targets and bumpers. The stage rails are intentionally
 * visual-only: they communicate the second level without creating new pockets
 * that can trap a slow ball.
 */
export function shapeHauntedKeep(table: TableDefinition): TableDefinition {
  const width = table.board.width;
  const height = table.board.height;
  const upperLeft = table.parts.find((part): part is FlipperPart => part.id === 'flip-upper-l' && part.kind === 'flipper-left');
  const upperRight = table.parts.find((part): part is FlipperPart => part.id === 'flip-upper-r' && part.kind === 'flipper-right');
  if (upperLeft) {
    upperLeft.pivot = { x: width * 0.31, y: height * 0.59 };
    upperLeft.length = Math.max(8.5, width * 0.145);
  }
  if (upperRight) {
    upperRight.pivot = { x: width * 0.69, y: height * 0.59 };
    upperRight.length = Math.max(8.5, width * 0.145);
  }

  const positions: Record<string, Vec2> = {
    'bumper-left': { x: width * 0.28, y: height * 0.22 },
    'bumper-right': { x: width * 0.72, y: height * 0.22 },
    'bumper-center': { x: width * 0.5, y: height * 0.31 },
    'target-1': { x: width * 0.31, y: height * 0.43 },
    'target-2': { x: width * 0.5, y: height * 0.405 },
    'target-3': { x: width * 0.69, y: height * 0.43 },
  };
  for (const [id, center] of Object.entries(positions)) {
    const part = table.parts.find((candidate) => candidate.id === id);
    if (part && 'center' in part) part.center = center;
  }

  const mainRamp = table.parts.find((part): part is RampPart => part.id === 'ramp-main' && part.kind === 'ramp');
  if (mainRamp) {
    mainRamp.entry = { x: width * 0.73, y: height * 0.72 };
    mainRamp.path = [
      { x: width * 0.73, y: height * 0.72 },
      { x: width * 0.8, y: height * 0.63 },
      { x: width * 0.79, y: height * 0.51 },
      { x: width * 0.74, y: height * 0.4 },
      { x: width * 0.65, y: height * 0.3 },
      { x: width * 0.56, y: height * 0.245 },
    ];
    mainRamp.exit = { ...mainRamp.path[mainRamp.path.length - 1]! };
    mainRamp.exitVelocity = { x: -width * 0.13, y: height * 0.17 };
  }

  const upperRamp = table.parts.find((part): part is RampPart => part.id === 'ramp-upper-loop' && part.kind === 'ramp');
  if (upperRamp) {
    upperRamp.style = 'wireform';
    upperRamp.width = Math.max(4.6, width * 0.075);
    upperRamp.entry = { x: width * 0.27, y: height * 0.59 };
    upperRamp.path = [
      { x: width * 0.27, y: height * 0.59 },
      { x: width * 0.2, y: height * 0.5 },
      { x: width * 0.21, y: height * 0.39 },
      { x: width * 0.3, y: height * 0.29 },
      { x: width * 0.44, y: height * 0.225 },
      { x: width * 0.56, y: height * 0.25 },
    ];
    upperRamp.exit = { ...upperRamp.path[upperRamp.path.length - 1]! };
    upperRamp.exitVelocity = { x: width * 0.12, y: height * 0.15 };
  }

  const stageRails: GuideRailPart[] = [
    {
      id: 'stage-left-rail',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.55,
      collider: false,
      material: 'metal',
      points: [
        { x: width * 0.09, y: height * 0.64 },
        { x: width * 0.16, y: height * 0.59 },
        { x: width * 0.25, y: height * 0.565 },
      ],
    },
    {
      id: 'stage-right-rail',
      kind: 'guide-rail',
      scoreValue: 0,
      thickness: 0.55,
      collider: false,
      material: 'metal',
      points: [
        { x: width * 0.91, y: height * 0.64 },
        { x: width * 0.84, y: height * 0.59 },
        { x: width * 0.75, y: height * 0.565 },
      ],
    },
  ];
  for (const rail of stageRails) {
    if (!table.parts.some((part) => part.id === rail.id)) table.parts.push(rail);
  }

  const rampInsert = table.parts.find((part): part is InsertPart => part.id === 'insert-ramp' && part.kind === 'insert');
  if (rampInsert) {
    rampInsert.center = { x: width * 0.73, y: height * 0.745 };
    const upperInsert: InsertPart = {
      ...rampInsert,
      id: 'insert-upper-ramp',
      center: { x: width * 0.27, y: height * 0.615 },
      color: table.theme.accentColor,
    };
    if (!table.parts.some((part) => part.id === upperInsert.id)) table.parts.push(upperInsert);
  }

  return table;
}
